import { useCallback, useEffect, useRef } from 'react'
import { useAppRoute } from '@/hooks/useAppRoute'
import {
  useLazyGetSessionQuery,
  useLogoutMutation,
} from '@/store/api/authApi'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import {
  selectAuthStatus,
  selectIsAuthenticated,
  selectSession,
  selectSessionDisplayName,
  selectSessionEmail,
} from '@/store/selectors/authSelectors'
import {
  authBootstrapStarted,
  logout as logoutAction,
  sessionMissing,
  sessionRestored,
} from '@/store/slices/authSlice'

export function useAuthSession() {
  const dispatch = useAppDispatch()
  const session = useAppSelector(selectSession)
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const displayName = useAppSelector(selectSessionDisplayName)
  const email = useAppSelector(selectSessionEmail)
  const { navigateToLogin } = useAppRoute()
  const [logoutRequest] = useLogoutMutation()

  const logout = useCallback(async () => {
    await logoutRequest()
    dispatch(logoutAction())
    navigateToLogin()
  }, [dispatch, logoutRequest, navigateToLogin])

  return {
    session,
    isAuthenticated,
    displayName,
    email,
    logout,
  }
}

export function useAuthBootstrap() {
  const dispatch = useAppDispatch()
  const status = useAppSelector(selectAuthStatus)
  const { isLoginRoute, navigateToLogin, navigateToShell } = useAppRoute()
  const [getSession] = useLazyGetSessionQuery()
  const bootstrapStarted = useRef(false)

  const bootstrap = useCallback(async () => {
    dispatch(authBootstrapStarted())
    const result = await getSession()

    if (result.data) {
      dispatch(sessionRestored(result.data))
      return
    }

    dispatch(sessionMissing())
  }, [dispatch, getSession])

  useEffect(() => {
    if (bootstrapStarted.current) {
      return
    }
    bootstrapStarted.current = true
    void bootstrap()
  }, [bootstrap])

  useEffect(() => {
    if (status === 'unknown') {
      return
    }

    if (status === 'unauthenticated' && !isLoginRoute) {
      navigateToLogin()
      return
    }

    if (
      status === 'authenticated' &&
      (isLoginRoute || window.location.pathname === '/')
    ) {
      navigateToShell()
    }
  }, [status, isLoginRoute, navigateToLogin, navigateToShell])

  return { status }
}
